<script setup lang="ts">
import { ShieldOff, X } from 'lucide-vue-next';
import { computed } from 'vue';
import { t } from '../i18n';
import type { Target } from '../types';

/**
 * `refusal` — the server's own sentence when the session cannot start: a seat somebody
 * else booked, most often. It used to arrive as a toast over a sheet that still offered
 * «Почати сесію», so the answer slid away in three seconds and the button invited another
 * try. Now it takes over the sheet the way «Доступ закрито» does: same shield, the reason
 * in the middle, one way out.
 */
const props = defineProps<{
    open: boolean;
    target: Target;
    busy?: boolean;
    refusal?: string | null;
}>();

/** Blocked in this club, or refused when starting — one scene, one button, either way. */
const closed = computed(() => props.target.blocked === true || !!props.refusal);
defineEmits<{ close: []; confirm: [] }>();
</script>

<template>
    <Transition name="sheet" appear>
        <div v-if="open" class="sheet-layer">
            <button
                class="sheet-scrim"
                type="button"
                :aria-label="t('Закрити')"
                @click="$emit('close')"
            />
            <section
                class="sheet"
                role="dialog"
                aria-modal="true"
                aria-labelledby="session-title"
            >
                <button
                    class="sheet-close"
                    type="button"
                    :aria-label="t('Закрити')"
                    :disabled="busy"
                    @click="$emit('close')"
                >
                    <X :size="19" />
                </button>
                <div v-if="closed" class="blocked-scene" aria-hidden="true">
                    <ShieldOff :size="64" :stroke-width="1.5" />
                </div>
                <div v-else class="pc-scene" aria-hidden="true">
                    <div class="pc-glow" />
                    <div class="monitor">
                        <div class="screen"><i /><i /><i /></div>
                        <div class="neck" />
                        <div class="stand" />
                    </div>
                    <div class="keyboard"><i v-for="n in 13" :key="n" /></div>
                </div>
                <div class="sheet-copy">
                    <span class="eyebrow">{{ target.club.name }}</span>
                    <template v-if="refusal">
                        <h2 id="session-title">
                            {{ t('Не вдалося почати сесію') }}
                        </h2>
                        <p>{{ refusal }}</p>
                    </template>
                    <template v-else-if="target.blocked">
                        <h2 id="session-title">{{ t('Доступ закрито') }}</h2>
                        <p>
                            {{
                                t(
                                    'Вас заблоковано в цьому клубі. Будь ласка, зверніться до адміністратора.',
                                )
                            }}
                        </p>
                    </template>
                    <template v-else>
                        <h2 id="session-title">{{ t('Почати сесію?') }}</h2>
                        <p>
                            {{
                                t(
                                    'Сесія розпочнеться за ПК {computer}{zone}. Кошти списуватимуться з балансу за тарифом клубу.',
                                    {
                                        computer: target.computer?.name ?? '',
                                        zone: target.computer?.zone
                                            ? t(' у зоні {zone}', {
                                                  zone: target.computer.zone,
                                              })
                                            : '',
                                    },
                                )
                            }}
                        </p>
                    </template>
                </div>
                <!-- Blocked: one way out and nothing to press by mistake. Offering the
                     start button to somebody the club has blocked is offering a button that
                     answers with an error. -->
                <div
                    class="session-actions"
                    :class="{ 'session-actions--single': closed }"
                >
                    <button
                        class="session-button session-button--cancel"
                        type="button"
                        :disabled="busy"
                        @click="$emit('close')"
                    >
                        {{ closed ? t('Зрозуміло') : t('Скасувати') }}
                    </button>
                    <button
                        v-if="!closed"
                        class="session-button session-button--start"
                        type="button"
                        :disabled="busy"
                        @click="$emit('confirm')"
                    >
                        <span v-if="busy" class="spinner" />
                        <span v-else>{{ t('Почати сесію') }}</span>
                    </button>
                </div>
            </section>
        </div>
    </Transition>
</template>

<style scoped>
.sheet-layer {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: flex-end;
}
.sheet-scrim {
    position: absolute;
    inset: 0;
    border: 0;
    background: rgba(4, 8, 18, 0.48);
    backdrop-filter: blur(5px);
}
.sheet {
    position: relative;
    width: 100%;
    max-height: 92dvh;
    padding: 30px 20px calc(18px + env(safe-area-inset-bottom));
    border-radius: 26px 26px 0 0;
    background: var(--surface);
    box-shadow: 0 -18px 70px rgba(0, 0, 0, 0.25);
    text-align: center;
    transform: translate3d(0, 0, 0);
    will-change: transform;
}
.sheet::before {
    content: '';
    position: absolute;
    top: 8px;
    left: 50%;
    width: 38px;
    height: 4px;
    transform: translateX(-50%);
    border-radius: 99px;
    background: color-mix(in srgb, var(--hint) 24%, transparent);
}
.sheet-close {
    position: absolute;
    top: 18px;
    right: 18px;
    display: grid;
    place-items: center;
    width: 34px;
    height: 34px;
    border: 0;
    border-radius: 50%;
    background: var(--secondary);
    color: var(--text);
}
.pc-scene {
    position: relative;
    height: 190px;
    display: grid;
    place-items: center;
    margin-top: 5px;
}
.pc-glow {
    position: absolute;
    width: 150px;
    height: 125px;
    border-radius: 45%;
    background: color-mix(in srgb, var(--accent) 24%, transparent);
    filter: blur(28px);
    animation: breathe 2s infinite ease-in-out;
}
.monitor {
    position: relative;
    z-index: 2;
    margin-top: -8px;
    animation: pc-float 2.8s infinite ease-in-out;
}
.screen {
    position: relative;
    width: 136px;
    height: 88px;
    overflow: hidden;
    border: 6px solid #17233a;
    border-radius: 13px;
    background: linear-gradient(145deg, #08284e, #2997ff 70%, #75c1ff);
    box-shadow:
        inset 0 0 25px rgba(255, 255, 255, 0.2),
        0 16px 32px rgba(41, 151, 255, 0.24);
}
.screen::after {
    content: '';
    position: absolute;
    inset: 0;
    background: linear-gradient(
        110deg,
        transparent 25%,
        rgba(255, 255, 255, 0.35) 44%,
        transparent 60%
    );
    transform: translateX(-120%);
    animation: shine 2.3s infinite;
}
.screen i {
    position: absolute;
    left: 18px;
    right: 18px;
    height: 4px;
    border-radius: 99px;
    background: rgba(255, 255, 255, 0.8);
    animation: code 1.8s infinite ease-in-out;
}
.screen i:nth-child(1) {
    top: 22px;
    right: 48px;
}
.screen i:nth-child(2) {
    top: 36px;
    left: 35px;
}
.screen i:nth-child(3) {
    top: 50px;
    right: 30px;
}
.neck {
    width: 16px;
    height: 18px;
    margin: auto;
    background: #17233a;
}
.stand {
    width: 62px;
    height: 7px;
    margin: auto;
    border-radius: 8px;
    background: #17233a;
}
.keyboard {
    position: absolute;
    z-index: 3;
    top: 136px;
    display: grid;
    grid-template-columns: repeat(7, 13px);
    gap: 4px;
    padding: 8px 12px;
    border-radius: 8px;
    background: #17233a;
    transform: perspective(100px) rotateX(18deg);
    box-shadow: 0 9px 18px rgba(0, 0, 0, 0.2);
}
.keyboard i {
    height: 5px;
    border-radius: 2px;
    background: #48617e;
}
.keyboard i:nth-child(4n) {
    background: #2997ff;
    box-shadow: 0 0 6px #2997ff;
}
.sheet-copy {
    margin: 4px 0 28px;
}
.eyebrow {
    color: var(--accent);
    font-size: 12px;
    font-weight: 650;
    letter-spacing: 0.04em;
    text-transform: uppercase;
}
.sheet h2 {
    margin: 11px 0 13px;
    font-size: 24px;
    line-height: 1.22;
}
.sheet p {
    margin: 0;
    color: var(--hint);
    font-size: 14px;
    line-height: 1.68;
}
.sheet p b {
    color: var(--text);
    font-weight: 600;
}
/* Where the animated PC stands when the club has closed the door on somebody. It does NOT
   borrow the PC scene's 190 px: that height is what the monitor and keyboard need, and a
   shield lost in it left a hole between the icon and the sentence explaining it. A target
   is blocked or not for the whole life of the sheet, so the two never swap and there is no
   jump to guard against. */
.blocked-scene {
    display: grid;
    place-items: center;
    padding: 20px 0 8px;
    color: #e5484d;
}
.session-actions {
    display: grid;
    grid-template-columns: 0.92fr 1.35fr;
    gap: 9px;
}
.session-actions--single {
    grid-template-columns: 1fr;
}
.session-button {
    display: flex;
    align-items: center;
    justify-content: center;
    min-width: 0;
    height: 50px;
    padding: 0 12px;
    border: 0;
    border-radius: 13px;
    color: white;
    font: 600 13px/1 var(--app-font);
}
.session-button:disabled {
    opacity: 0.68;
}
.session-button--cancel {
    background: #e5484d;
}
.session-button--start {
    background: #25a55f;
    box-shadow: 0 10px 24px rgba(37, 165, 95, 0.22);
}
.sheet-enter-active,
.sheet-leave-active {
    transition: opacity 0.24s ease;
}
.sheet-enter-active .sheet,
.sheet-leave-active .sheet {
    transition: transform 0.34s cubic-bezier(0.22, 0.88, 0.28, 1);
}
.sheet-enter-from,
.sheet-leave-to {
    opacity: 0;
}
.sheet-enter-from .sheet,
.sheet-leave-to .sheet {
    transform: translate3d(0, 100%, 0);
}
@keyframes breathe {
    50% {
        transform: scale(1.12);
        opacity: 0.65;
    }
}
@keyframes shine {
    55%,
    100% {
        transform: translateX(130%);
    }
}
@keyframes code {
    50% {
        opacity: 0.55;
        transform: translateX(4px);
    }
}
@keyframes pc-float {
    50% {
        transform: translateY(-6px) rotateY(-3deg);
    }
}
@media (prefers-reduced-motion: reduce) {
    .pc-glow,
    .monitor,
    .screen::after,
    .screen i {
        animation: none;
    }
}
</style>
